import { MapPin, Pencil } from 'lucide-react';

import { getClienteNotes, TIPO_PF, type Cliente } from '@/api/clientes';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { formatDate } from '@/lib/formatters';

import { AtendimentoDoCliente } from '@/pages/secretario/AtendimentoDoCliente';

import { ESTADOS_CIVIS } from '../form/clienteFormUtils';

// --- Helpers ---

function formatEndereco(c: Cliente): string | null {
  const parts = [
    c.endereco_logradouro,
    c.endereco_numero ? `n° ${c.endereco_numero}` : null,
    c.endereco_complemento,
    c.endereco_bairro,
    c.endereco_cidade,
    c.endereco_uf,
    c.endereco_cep,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

function formatEstadoCivil(value: string | null | undefined): string | null {
  if (!value) return null;
  const found = ESTADOS_CIVIS.find((e) => e.value === value);
  return found ? found.label : value;
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatTempoCliente(createdAt: string): string {
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 1) return 'desde hoje';
  if (diffDays === 1) return 'há 1 dia';
  if (diffDays < 30) return `há ${diffDays} dias`;

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `há ${diffMonths} ${diffMonths === 1 ? 'mês' : 'meses'}`;

  const years = Math.floor(diffMonths / 12);
  const remaining = diffMonths % 12;
  const yLabel = years === 1 ? 'ano' : 'anos';
  if (remaining === 0) return `há ${years} ${yLabel}`;
  const mLabel = remaining === 1 ? 'mês' : 'meses';
  return `há ${years} ${yLabel} e ${remaining} ${mLabel}`;
}

function buildGoogleMapsUrl(c: Cliente): string | null {
  const parts = [
    c.endereco_logradouro,
    c.endereco_numero,
    c.endereco_bairro,
    c.endereco_cidade,
    c.endereco_uf,
    c.endereco_cep,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
}

// --- Sub-components ---

function InfoField({ label, value, copyable, href, showEmpty }: {
  label: string;
  value?: string | null;
  copyable?: boolean;
  href?: string;
  /** Se true, mostra "Não informado" em itálico quando o valor é vazio */
  showEmpty?: boolean;
}) {
  if (!value && !showEmpty) return null;
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      {value ? (
        <div className="flex items-center gap-1">
          {href ? (
            <a href={href} className="text-sm text-primary hover:underline" target="_blank" rel="noopener noreferrer">{value}</a>
          ) : (
            <p className="text-sm">{value}</p>
          )}
          {copyable && <CopyButton text={value} className="h-6 w-6 [&_svg]:h-3 [&_svg]:w-3" />}
        </div>
      ) : (
        <p className="text-sm italic text-muted-foreground">Não informado</p>
      )}
    </div>
  );
}

function SectionHeader({ title, onEdit }: { title: string; onEdit?: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        {title}
      </h3>
      {onEdit && (
        <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onEdit}>
          <Pencil className="mr-0.5 h-3 w-3" />
          Editar
        </Button>
      )}
    </div>
  );
}

// --- Main component ---

interface ClienteDadosContentProps {
  cliente: Cliente;
  /** Callback para editar, recebe o step do formulário (0=Identificação, 1=Contato, 2=Endereço, 3=Complementar) */
  onEditStep?: (step: number) => void;
}

export function ClienteDadosContent({ cliente, onEditStep }: ClienteDadosContentProps) {
  const isPF = cliente.tipo_pessoa === TIPO_PF;
  const endereco = formatEndereco(cliente);
  const notes = getClienteNotes(cliente);
  const mapsUrl = buildGoogleMapsUrl(cliente);
  return (
    <div className="space-y-5">
      {/* O atendimento pelo Secretário que criou (ou reaproveitou) este cadastro:
          uma linha, não uma aba — os documentos já estão em "Documentos". */}
      <AtendimentoDoCliente clienteId={cliente.id} />
      {/* Identificacao */}
      <section className="space-y-2">
        <SectionHeader title="Identificação" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <InfoField label="Tipo" value={isPF ? 'Pessoa Física' : 'Pessoa Jurídica'} />
          {isPF ? (
            <>
              <InfoField label="CPF" value={cliente.cpf} copyable />
              <InfoField label="RG" value={cliente.rg} copyable />
            </>
          ) : (
            <>
              <InfoField label="Razão Social" value={cliente.razao_social} />
              <InfoField label="CNPJ" value={cliente.cnpj} copyable />
            </>
          )}
          {cliente.escritorio_nome && (
            <InfoField label="Escritório" value={cliente.escritorio_nome} />
          )}
        </div>
      </section>

      {/* Contato */}
      <section className="space-y-2">
        <SectionHeader title="Contato" onEdit={onEditStep ? () => onEditStep(1) : undefined} />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <InfoField label="E-mail" value={cliente.email} copyable href={cliente.email ? `mailto:${cliente.email}` : undefined} showEmpty />
          <InfoField label="Telefone" value={cliente.telefone} copyable href={cliente.telefone ? `tel:${cliente.telefone}` : undefined} showEmpty />
          <InfoField label="Telefone 2" value={cliente.telefone2} copyable href={cliente.telefone2 ? `tel:${cliente.telefone2}` : undefined} showEmpty />
        </div>
      </section>

      {/* Endereco */}
      <section className="space-y-2">
        <SectionHeader title="Endereço" onEdit={onEditStep ? () => onEditStep(2) : undefined} />
        {endereco ? (
          <div className="flex items-start gap-2">
            <p className="text-sm flex-1">{endereco}</p>
            <div className="flex items-center gap-0.5 shrink-0">
              <CopyButton text={endereco} label="Copiar endereço" className="h-6 w-6 [&_svg]:h-3 [&_svg]:w-3" />
              {mapsUrl && (
                <Button variant="ghost" size="icon" className="h-6 w-6" asChild>
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer" title="Abrir no Google Maps">
                    <MapPin className="h-3 w-3" />
                  </a>
                </Button>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm italic text-muted-foreground">Não informado</p>
        )}
      </section>

      {/* Complementar (PF) */}
      {isPF && (
        <section className="space-y-2">
          <SectionHeader title="Complementar" onEdit={onEditStep ? () => onEditStep(3) : undefined} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <InfoField label="Profissão" value={cliente.profissao} showEmpty />
            <InfoField label="Estado Civil" value={formatEstadoCivil(cliente.estado_civil)} showEmpty />
            <InfoField label="Data de Nascimento" value={cliente.data_nascimento ? formatDate(cliente.data_nascimento) : null} showEmpty />
            <InfoField label="Nacionalidade" value={cliente.nacionalidade} showEmpty />
          </div>
        </section>
      )}

      {/* Anotacoes */}
      {notes && (
        <section className="space-y-2">
          <SectionHeader title="Anotações" />
          <p className="text-sm whitespace-pre-wrap text-muted-foreground">{notes}</p>
        </section>
      )}

      {/* Timestamps */}
      <section className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground pt-2 border-t border-border/40">
        <span>Criado em {formatDateTime(cliente.created_at)}</span>
        <span>Atualizado em {formatDateTime(cliente.updated_at)}</span>
        <span>Cliente {formatTempoCliente(cliente.created_at)}</span>
      </section>
    </div>
  );
}
